import { useMutation } from '@tanstack/react-query';
import { useToast } from './useToast';
import { api, ApiError } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import type { DeviceStatus } from '@/lib/types';

interface MutationOptions<TVars> {
  successToast?: string | ((vars: TVars) => string);
  errorToast?: string | ((err: Error, vars: TVars) => string);
  optimistic?: (prev: DeviceStatus | undefined, vars: TVars) => DeviceStatus | undefined;
  invalidateAfterMs?: number;
}

// One hook for every /api/control/:clientId/<endpoint> POST. Keeps optimistic
// updates + toasts + cache invalidation in one place so each call site is one line.
export function useDeviceMutation<TVars extends object = object>(
  clientId: string,
  endpoint: string,
  opts: MutationOptions<TVars> = {},
) {
  const toast = useToast();
  const key = ['device-status', clientId];

  return useMutation({
    mutationFn: async (vars: TVars) =>
      api(`/api/control/${clientId}/${endpoint}`, { method: 'POST', body: vars }),
    onMutate: async (vars) => {
      if (!opts.optimistic) return;
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<DeviceStatus>(key);
      const next = opts.optimistic(prev, vars);
      if (next) queryClient.setQueryData(key, next);
      return { prev };
    },
    onError: (err, vars, ctx) => {
      // Roll back optimistic update
      if (ctx && 'prev' in ctx && ctx.prev) {
        queryClient.setQueryData(key, ctx.prev);
      }
      const message =
        typeof opts.errorToast === 'function' ? opts.errorToast(err as Error, vars)
        : opts.errorToast ?? (err instanceof ApiError ? err.message : 'Action failed');
      toast.error(message);
    },
    onSuccess: (_, vars) => {
      if (opts.successToast) {
        const t = typeof opts.successToast === 'function' ? opts.successToast(vars) : opts.successToast;
        toast.ok(t);
      }
      // Refresh from server after a short delay so the device's actual state
      // gets reflected (round-trip through the command queue).
      setTimeout(() => queryClient.invalidateQueries({ queryKey: key }), opts.invalidateAfterMs ?? 1500);
    },
  });
}
